import type { Schedule, Trip, StopId } from './types.js';

export function nowInMinutes(date: Date = new Date()): number {
  return date.getHours() * 60 + date.getMinutes();
}

export function timeToMinutes(time: string | null): number | null {
  if (time === null) return null;
  const match = time.match(/^(\d{2}):(\d{2})$/);
  if (!match || !match[1] || !match[2]) return null;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function tripsForStop(schedule: Schedule, stop: StopId): Trip[] {
  const tripsWithTime = schedule.trips
    .map((trip) => ({ trip, time: timeToMinutes(trip[stop]) }))
    .filter(({ time }) => time !== null) as { trip: Trip; time: number }[];
  tripsWithTime.sort((a, b) => a.time - b.time);
  return tripsWithTime.map(({ trip }) => trip);
}

export function nextTrip(schedule: Schedule, stop: StopId, now?: Date): Trip | null {
  const currentTime = nowInMinutes(now);
  for (const trip of tripsForStop(schedule, stop)) {
    const tripTime = timeToMinutes(trip[stop]);
    if (tripTime !== null && tripTime >= currentTime) return trip;
  }
  return null;
}

export function upcomingTrips(schedule: Schedule, stop: StopId, count: number, now?: Date): Trip[] {
  const currentTime = nowInMinutes(now);
  const upcoming: Trip[] = [];
  for (const trip of tripsForStop(schedule, stop)) {
    const tripTime = timeToMinutes(trip[stop]);
    if (tripTime !== null && tripTime >= currentTime) {
      upcoming.push(trip);
      if (upcoming.length >= count) break;
    }
  }
  return upcoming;
}

export function minutesUntilTrip(trip: Trip, stop: StopId, now?: Date): number | null {
  const tripTime = timeToMinutes(trip[stop]);
  if (tripTime === null) return null;
  const diff = tripTime - nowInMinutes(now);
  return diff < 0 ? null : diff;
}

function getHoursText(hours: number): string {
  if (hours >= 11 && hours <= 14) return `${hours} часов`;
  const d = hours % 10;
  if (d === 1) return `${hours} час`;
  if (d >= 2 && d <= 4) return `${hours} часа`;
  return `${hours} часов`;
}

export function formatCountdown(minutes: number): string {
  if (minutes <= 0) return 'уже здесь';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return mins === 1 ? 'через 1 мин' : `через ${mins} мин`;
  const hoursText = getHoursText(hours);
  if (mins === 0) return `через ${hoursText}`;
  return mins === 1 ? `через ${hoursText} 1 мин` : `через ${hoursText} ${mins} мин`;
}
